/**
 * feat-270 — what the detail panel knows about the item being edited: is it on
 * phones, and if not, what ARE employees reading?
 *
 * `design/editor-integration.html` panels 3, 4 and 9. They are one job, because
 * all three are states of this same question — divergence, never-approved, and
 * "we could not find out".
 *
 * ⚠ **§10.10 — ABSENCE OF A MARKER IS A CLAIM.** Approval state comes from a
 * different call than the item. If that call fails and the panel simply draws no
 * markers, it is indistinguishable from "everything matches" — and it would be
 * telling an editor their draft is live when it may not be. So `error` is its own
 * state with its own visible alert, and it takes `danger-*`, not `attention-*`: a
 * failed fetch is an engineering fault the author cannot fix from that panel.
 * **Editing stays enabled throughout** — the panel's job is to stop asserting,
 * not to stop working.
 *
 * ⚠ **§10.8 / panel 4 — never-approved SKIPS field diffing entirely.** It does not
 * diff against null. There is no approved snapshot, so per-field divergence is
 * not empty, it is *meaningless*: "On phones now: —" under every field asserts
 * that we know the learner is reading a blank, when the truth is they are not
 * reading this item at all. One panel-level sentence, no per-field lines, no
 * markers. The state machine is `never_approved → skip`, never
 * `never_approved → diff against null`.
 *
 * ⚠ No comparison here either. Which fields differ is `fields[].changed` from the
 * server (ADR-B §2.1); this module looks the item up and hands the flag on.
 */

import React, { createContext, useContext, useMemo } from 'react';
import { APPROVAL_EDITOR_COPY as E } from '../approvalCopy';
import { CHANGED_FIELD_ROW_CLASS, ChangedDot, DIFF_MARKER_CLASS } from '../../curriculumDiffPresentation';
import { APPROVAL_QUEUE_COPY } from '../approvalCopy';
import { CriteriaList } from './criteriaValue';
import type {
  ApprovalField,
  ApprovalPendingItem,
  LessonApprovalStatus,
} from '../../../../services/curriculumApprovalService';

export interface ItemApprovalState {
  /**
   * `off`     — the tenant flag is off, or nothing is selected. Render nothing;
   *             the editor is exactly today's editor (AT-29).
   * `loading` — not known yet. Render nothing, and assert nothing.
   * `error`   — the fetch failed. Render the ALERT, and no markers.
   * `ready`   — we know.
   */
  status: 'off' | 'loading' | 'error' | 'ready';
  /** The selected item's lesson. `never_approved` suppresses all field diffing. */
  lessonState: LessonApprovalStatus | null;
  lessonTitle: string | null;
  /** The pending item for the entity currently in the panel, if it is pending. */
  item: ApprovalPendingItem | null;
  onRetry?: () => void;
}

const OFF: ItemApprovalState = {
  status: 'off',
  lessonState: null,
  lessonTitle: null,
  item: null,
};

const ItemApprovalContext = createContext<ItemApprovalState>(OFF);

export const ItemApprovalProvider: React.FC<{
  value: ItemApprovalState;
  children: React.ReactNode;
}> = ({ value, children }) => (
  <ItemApprovalContext.Provider value={value}>{children}</ItemApprovalContext.Provider>
);

export function useItemApproval(): ItemApprovalState {
  return useContext(ItemApprovalContext);
}

/**
 * Look up one gated column on the item in the panel.
 *
 * Returns `null` in every state where a marker would be a CLAIM we cannot back:
 * flag off, loading, failed fetch, never-approved (§10.8), item not pending, or
 * the server saying this particular field did not change.
 */
function useField(field: string): ApprovalField | null {
  const { status, lessonState, item } = useItemApproval();
  return useMemo(() => {
    if (status !== 'ready') return null;
    if (lessonState === 'never_approved') return null;
    if (!item) return null;
    // `created` means never approved at the ITEM level — same rule, same reason.
    if (item.change_kind === 'created') return null;
    const found = item.fields.find((f) => f.field === field);
    return found?.changed ? found : null;
  }, [status, lessonState, item, field]);
}

/**
 * §10.3 — the marker rides the LABEL, never the input's border.
 *
 * `fieldBorder()` (`QuestionDetailForm.tsx:216-219`) is already three-way:
 * `border-attention-line` for validation, `border-danger-line` for a failed save,
 * `border-softBorder` otherwise. A fourth state on the same property would fight
 * the two that report a FAILURE, and a failure must win.
 *
 * Same `ChangedDot` + marker-class vocabulary as the queue and the History diff,
 * so one glyph means "changed" everywhere in this feature — with this surface's
 * word, `differs`, for the reason in `approvalCopy.ts`.
 */
export const ApprovedFieldMarker: React.FC<{ field: string }> = ({ field }) => {
  const found = useField(field);
  if (!found) return null;
  return (
    <span className="inline-flex items-center gap-1 ml-1.5 font-normal">
      <ChangedDot />
      <span className={DIFF_MARKER_CLASS}>{APPROVAL_QUEUE_COPY.differs}</span>
    </span>
  );
};

/**
 * §10.3 ③ — the approved value itself, printed under the field. **Nothing else
 * closes AC3**: a marker says *something* differs; only the value says *what the
 * learner is reading right now*.
 *
 * ⚠ **§10.4 — THIS MUST RENDER INSIDE THE FIELD'S OWN `<div>`.** The panel's
 * scroll area is `p-5 space-y-4`, and Tailwind emits
 * `.space-y-4 > :not([hidden]) ~ :not([hidden])` at specificity **(0,3,0)** while
 * `-mt-1.5` is **(0,1,0)**. Specificity decides, source order is irrelevant, and
 * nothing warns — feat-252 shipped two inert negative margins at exactly this
 * seam. As a direct child of the `space-y-4` this line gets 16px of gap and reads
 * as a separate FIELD rather than as a fact about the one above it.
 *
 * ⚠ **§10.5 — when the values match, render NOTHING, and "nothing" means
 * `return null`.** In a `space-y-*` parent an empty `<div>` still consumes a gap,
 * so the panel gets a hole where the suppressed line was.
 */
export const ApprovedValueLine: React.FC<{ field: string }> = ({ field }) => {
  const found = useField(field);
  if (!found) return null;

  const value = found.approved_value;
  const empty =
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0);

  return (
    <div
      data-testid={`approved-value-${field}`}
      className={`mt-1.5 text-xs text-inkMuted ${CHANGED_FIELD_ROW_CLASS}`}
    >
      <span className="font-semibold">{E.onPhonesNow}</span>{' '}
      {/* §10.7 — an empty approved value prints a SENTENCE, not a dash. A dash
          asserts ignorance ("we don't know"); the sentence asserts knowledge
          ("we looked, and it was empty"). This is the mirror image of §10.5 and
          the two are easy to swap by accident. */}
      {empty ? (
        <em>{APPROVAL_QUEUE_COPY.emptyApprovedValue}</em>
      ) : Array.isArray(value) ? (
        // A rehearsal's criteria rubric. `String(value)` would print "a,b,c" and
        // lose the ordering that ADR-A §5.1 makes part of its meaning.
        <CriteriaList value={value} />
      ) : (
        <span className="break-words whitespace-pre-wrap">{String(value)}</span>
      )}
    </div>
  );
};

/**
 * The ONE panel-level block. Panels 4 and 9.
 *
 * ⚠ One `attention-*` block per panel, NEVER one per field. Six amber blocks in a
 * 380px column is the wall `autoSaveCopy.ts`'s "one banner, never a stack"
 * already forbids.
 */
export const PanelApprovalState: React.FC = () => {
  const { status, lessonState, lessonTitle, onRetry } = useItemApproval();

  // §10.10 / panel 9. The fetch failed, so the panel must SAY it does not know.
  // `danger-*`, not `attention-*`: the author cannot fix this from here.
  if (status === 'error') {
    return (
      <div
        role="alert"
        data-testid="approval-state-error"
        className="rounded-xl bg-danger-surface border border-danger-line/40 text-danger-text px-3 py-2 text-[11.5px] leading-snug"
      >
        {E.stateFetchFailed}
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="ml-1.5 font-semibold underline"
          >
            {E.stateFetchRetry}
          </button>
        )}
      </div>
    );
  }

  if (status !== 'ready') return null;

  // §10.8 / panel 4. No per-field lines anywhere beneath this — `useField`
  // returns null for every field in this state — and one sentence that states the
  // whole situation. Copy names the CONSEQUENCE in the reader's vocabulary
  // ("employees can't see it yet"), not the internal status word.
  if (lessonState === 'never_approved') {
    return (
      <div
        data-testid="approval-state-never-approved"
        className="rounded-xl bg-attention-surface text-attention-on border border-attention-line/50 px-3 py-2 text-[11.5px] leading-snug"
      >
        <strong className="font-bold">
          {lessonTitle
            ? `Employees can’t see any of “${lessonTitle}” yet.`
            : E.panelNeverApproved}
        </strong>{' '}
        Nothing in this Lesson has ever been approved, so none of it is on phones.
        Someone with approval rights has to approve the Lesson once; after that,
        only the parts you change need approving again.
      </div>
    );
  }

  return null;
};
